import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import './Chat.scss'

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
)


const AttachIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
  </svg>
)

const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
const IS_ANDROID = /Android/i.test(UA)
const IS_IOS = /iPhone|iPad|iPod/i.test(UA)
const IS_IN_APP_BROWSER = /FBAN|FBAV|FB_IAB|Instagram|Line\//i.test(UA)

const openExternally = (url) => {
  if (IS_ANDROID) {
    const withoutScheme = url.replace(/^https?:\/\//, '')
    window.location.href = `intent://${withoutScheme}#Intent;scheme=https;package=com.android.chrome;end;`
    return
  }
  if (IS_IOS) {
    window.location.href = url.replace(/^https/, 'x-safari-https')
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}


const Chat = () => {
  const { jobId } = useParams()

  const clientId = useMemo(() => {
    let id = localStorage.getItem('gappeo_webchat_client_id')
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      localStorage.setItem('gappeo_webchat_client_id', id)
    }
    return id
  }, [])

  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  const [refToken, setRefToken] = useState('')
  const [wsSessionToken, setWsSessionToken] = useState('')
  const [externalUserId, setExternalUserId] = useState('')
  const [isBotTyping, setIsBotTyping] = useState(false)
  const [pendingAttachment, setPendingAttachment] = useState(null)
  const [isProcessingResume, setIsProcessingResume] = useState(false)
  const [evaluationCountdown, setEvaluationCountdown] = useState(null)
  const [isReconnecting, setIsReconnecting] = useState(false)

  const wsRef = useRef(null)
  const chatMessagesRef = useRef(null)
  const fileInputRef = useRef(null)
  const containerRef = useRef(null)
  const chatInputRef = useRef(null)
  const chatInputAreaRef = useRef(null)
  const evaluationIntervalRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const shouldReconnectRef = useRef(true)
  const heartbeatIntervalRef = useRef(null)
  const heartbeatTimeoutRef = useRef(null)

  const MAX_RECONNECT_DELAY_MS = 30000
  const HEARTBEAT_INTERVAL_MS = 5000
  const HEARTBEAT_TIMEOUT_MS = 2000
  const KEYBOARD_SHRINK_THRESHOLD_PX = 120
  const PIN_IDLE_RELEASE_MS = 4000
  // These in-app WebViews never resize the viewport for the keyboard, so its
  // real height can't be measured -- this is a fixed estimate covering the
  // system keyboard + QuickType suggestion bar across current iPhone sizes.
  // Anchoring from the bottom (rather than a top offset into the viewport)
  // means the bar sits just above the keyboard regardless of the bar's own
  // height, instead of floating at a fraction of the screen.
  const KEYBOARD_HEIGHT_ESTIMATE_PX = 300
  const computePinBottomPx = () => Math.min(KEYBOARD_HEIGHT_ESTIMATE_PX, window.innerHeight * 0.5)

  const wsUrl = `${process.env.REACT_APP_WS_URL}?type=WEBCHAT&webchat_client_id=${encodeURIComponent(clientId)}`
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]')
    if (!meta) return undefined
    const original = meta.getAttribute('content') || ''
    if (!/interactive-widget=/.test(original)) {
      meta.setAttribute('content', `${original}, interactive-widget=resizes-content`)
    }
    return () => {
      meta.setAttribute('content', original)
    }
  }, [])

  useEffect(() => {
    const container = chatMessagesRef.current
    if (!container) return
    const frame = requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight
    })
    return () => cancelAnimationFrame(frame)
  }, [messages, isBotTyping, isProcessingResume, evaluationCountdown, pendingAttachment])

  const lastViewportHeightRef = useRef(null)
  const lastViewportOffsetRef = useRef(0)
  const viewportRafRef = useRef(null)
  const scrollIntoViewRafRef = useRef(null)
  const keyboardPollRef = useRef(null)
  const focusTimeoutsRef = useRef([])
  const keyboardOverlayHeightRef = useRef(0)
  const isInputFocusedRef = useRef(false)
  const preFocusViewportHeightRef = useRef(null)
  const isPinnedAboveKeyboardRef = useRef(false)
  const pinIdleTimeoutRef = useRef(null)

  const setPinnedAboveKeyboard = useCallback((pinned) => {
    if (pinned === isPinnedAboveKeyboardRef.current) return
    isPinnedAboveKeyboardRef.current = pinned

    if (pinIdleTimeoutRef.current) {
      clearTimeout(pinIdleTimeoutRef.current)
      pinIdleTimeoutRef.current = null
    }

    chatInputAreaRef.current?.classList.toggle('chat-input-area--pinned-above-keyboard', pinned)
    chatMessagesRef.current?.classList.toggle('chat-messages--reserve-pinned-input', pinned)

    if (pinned) {
      const el = containerRef.current
      if (el) el.style.setProperty('--gp-pin-bottom', `${computePinBottomPx()}px`)

      pinIdleTimeoutRef.current = setTimeout(() => setPinnedAboveKeyboard(false), PIN_IDLE_RELEASE_MS)
    }

    const messagesEl = chatMessagesRef.current
    if (messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight
  }, [])

  const notePinnedActivity = useCallback(() => {
    if (!isPinnedAboveKeyboardRef.current) return
    if (pinIdleTimeoutRef.current) clearTimeout(pinIdleTimeoutRef.current)
    pinIdleTimeoutRef.current = setTimeout(() => setPinnedAboveKeyboard(false), PIN_IDLE_RELEASE_MS)
  }, [setPinnedAboveKeyboard])

  const syncViewportAndScroll = useCallback(() => {
    if (viewportRafRef.current) return
    viewportRafRef.current = requestAnimationFrame(() => {
      viewportRafRef.current = null

      const overlayHeight = keyboardOverlayHeightRef.current
      const vh = overlayHeight > 0
        ? window.innerHeight - overlayHeight
        : (window.visualViewport?.height || window.innerHeight)

      if (vh !== lastViewportHeightRef.current) {
        lastViewportHeightRef.current = vh
        const el = containerRef.current
        if (el) el.style.setProperty('--gp-vh', `${vh}px`)

        const messagesEl = chatMessagesRef.current
        if (messagesEl) messagesEl.scrollTop = messagesEl.scrollHeight
      }

      // iOS WebKit quirk: position:fixed elements track the layout
      // viewport, not the visual one, so they don't move when Safari pans
      // the page to reveal a focused input above the keyboard. Even with
      // --gp-vh correctly shrunk to visualViewport.height, the box stays
      // glued to the old top edge, leaving a gap exactly offsetTop pixels
      // tall between it and the keyboard. Translating by that offset keeps
      // it following the pan. Android doesn't have this quirk (offsetTop is
      // always 0 there), so this is scoped to iOS only -- both to skip the
      // pointless writes and so .chart-container's transform (and the
      // compositing layer + fixed-position containing block it creates)
      // only exists on the platform that actually needs it.
      if (IS_IOS) {
        const offsetTop = window.visualViewport?.offsetTop || 0
        if (offsetTop !== lastViewportOffsetRef.current) {
          lastViewportOffsetRef.current = offsetTop
          const el = containerRef.current
          if (el) el.style.setProperty('--gp-vh-offset', `${offsetTop}px`)
        }
      }

      if (IS_IN_APP_BROWSER && isInputFocusedRef.current && preFocusViewportHeightRef.current != null) {
        const shrink = preFocusViewportHeightRef.current - vh
        setPinnedAboveKeyboard(shrink < KEYBOARD_SHRINK_THRESHOLD_PX)
      }
    })
  }, [setPinnedAboveKeyboard])
  const queueScrollIntoView = useCallback((target) => {
    if (scrollIntoViewRafRef.current) cancelAnimationFrame(scrollIntoViewRafRef.current)
    scrollIntoViewRafRef.current = requestAnimationFrame(() => {
      scrollIntoViewRafRef.current = null
      target.scrollIntoView({ block: 'nearest' })
    })
  }, [])

  const stopKeyboardPolling = useCallback(() => {
    if (keyboardPollRef.current) {
      clearInterval(keyboardPollRef.current)
      keyboardPollRef.current = null
    }
    focusTimeoutsRef.current.forEach(clearTimeout)
    focusTimeoutsRef.current = []
    isInputFocusedRef.current = false
    preFocusViewportHeightRef.current = null
    setPinnedAboveKeyboard(false)

    // Blurring the input is an unambiguous "the keyboard is closing" signal
    // on iOS (unlike the WebViews the rest of this file works around, where
    // even that can be unreliable). visualViewport's own resize/scroll
    // events should already bring offsetTop back to 0 when that happens,
    // but force it here too as a guaranteed floor -- if it were ever missed,
    // the entire chat would stay shifted downward after the keyboard closes.
    if (IS_IOS) {
      lastViewportOffsetRef.current = 0
      containerRef.current?.style.setProperty('--gp-vh-offset', '0px')
    }
  }, [setPinnedAboveKeyboard])

  const startKeyboardPolling = useCallback((target) => {
    stopKeyboardPolling()

    isInputFocusedRef.current = true
    preFocusViewportHeightRef.current = window.visualViewport?.height || window.innerHeight

    keyboardPollRef.current = setInterval(syncViewportAndScroll, 300)
    focusTimeoutsRef.current = [0, 100, 250, 400, 600, 900].map(delay => setTimeout(() => {
      syncViewportAndScroll()
      queueScrollIntoView(target)
    }, delay))
  }, [syncViewportAndScroll, queueScrollIntoView, stopKeyboardPolling])

  useEffect(() => {
    syncViewportAndScroll()
    window.visualViewport?.addEventListener('resize', syncViewportAndScroll)
    // offsetTop (how far Safari has panned the visual viewport) changes fire
    // 'scroll' on visualViewport, not 'resize' -- both are needed to catch
    // the iOS pan-without-resize gap described above.
    window.visualViewport?.addEventListener('scroll', syncViewportAndScroll)
    window.addEventListener('resize', syncViewportAndScroll)

    return () => {
      window.visualViewport?.removeEventListener('resize', syncViewportAndScroll)
      window.visualViewport?.removeEventListener('scroll', syncViewportAndScroll)
      window.removeEventListener('resize', syncViewportAndScroll)
      stopKeyboardPolling()
      if (viewportRafRef.current) cancelAnimationFrame(viewportRafRef.current)
      if (scrollIntoViewRafRef.current) cancelAnimationFrame(scrollIntoViewRafRef.current)
    }
  }, [syncViewportAndScroll, stopKeyboardPolling])

  useEffect(() => {
    if (!('virtualKeyboard' in navigator)) return undefined

    navigator.virtualKeyboard.overlaysContent = true
    const handleGeometryChange = () => {
      keyboardOverlayHeightRef.current = navigator.virtualKeyboard.boundingRect?.height || 0
      syncViewportAndScroll()
    }
    navigator.virtualKeyboard.addEventListener('geometrychange', handleGeometryChange)

    return () => {
      navigator.virtualKeyboard.removeEventListener('geometrychange', handleGeometryChange)
      navigator.virtualKeyboard.overlaysContent = false
      keyboardOverlayHeightRef.current = 0
    }
  }, [syncViewportAndScroll])

  useEffect(() => {
    const handleOutsideTouch = (e) => {
      if (!isPinnedAboveKeyboardRef.current) return
      if (chatInputAreaRef.current?.contains(e.target)) return
      setPinnedAboveKeyboard(false)
    }
    document.addEventListener('touchstart', handleOutsideTouch, { passive: true })
    return () => document.removeEventListener('touchstart', handleOutsideTouch)
  }, [setPinnedAboveKeyboard])

  const clearEvaluationCountdown = useCallback(() => {
    if (evaluationIntervalRef.current) {
      clearInterval(evaluationIntervalRef.current)
      evaluationIntervalRef.current = null
    }
    setEvaluationCountdown(null)
  }, [])

  const startEvaluationCountdown = useCallback(() => {
    clearEvaluationCountdown()
    setEvaluationCountdown(30)
    evaluationIntervalRef.current = setInterval(() => {
      setEvaluationCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(evaluationIntervalRef.current)
          evaluationIntervalRef.current = null
          return null
        }
        return prev - 1
      })
    }, 1000)
  }, [clearEvaluationCountdown])

  const getBotText = (response) => response?.template?.text?.trim() || response?.text || ''

  const appendBotResponse = useCallback((list, response) => {
    const newText = getBotText(response)
    const last = list[list.length - 1]
    const lastText = last?.role === 'bot' ? getBotText(last.response) : null
    if (lastText !== null && lastText === newText) return list
    return [...list, { role: 'bot', response, ts: Date.now() }]
  }, [])

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
      heartbeatTimeoutRef.current = null
    }
  }, [])

  const startHeartbeat = useCallback((ws) => {
    stopHeartbeat()
    heartbeatIntervalRef.current = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return
      if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current)
      ws.send(JSON.stringify({ action: 'ping' }))
      heartbeatTimeoutRef.current = setTimeout(() => {
        ws.close()
      }, HEARTBEAT_TIMEOUT_MS)
    }, HEARTBEAT_INTERVAL_MS)
  }, [stopHeartbeat])

  const connectWs = useCallback(() => {
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0
      setIsReconnecting(false)
      startHeartbeat(ws)
    }

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data)

        if (data.type === 'pong') {
          if (heartbeatTimeoutRef.current) {
            clearTimeout(heartbeatTimeoutRef.current)
            heartbeatTimeoutRef.current = null
          }
          return
        }

        if (data.type === 'your_id') {
          const extId = `webchat-${clientId}`
          setExternalUserId(extId)
          ws.send(JSON.stringify({
            action: 'webchat_init',
            job_id: jobId,
            external_user_id: extId,
            metadata: { source: 'apply_page' },
          }))
          setIsBotTyping(true)
        }

        if (data.type === 'webchat_response') {
          clearEvaluationCountdown()

          if (data.external_user_id) setExternalUserId(data.external_user_id)
          if (data.ref_token) setRefToken(data.ref_token)
          if (data.ws_session_token) setWsSessionToken(data.ws_session_token)

          const historyMessages = (data.chat_history?.length > 0)
            ? data.chat_history.map((item, i, arr) => {
                if (item.direction === 'outbound') {
                  const wasAnswered = arr[i + 1]?.direction === 'inbound'
                  return {
                    role: 'bot',
                    response: {
                      text: item.text,
                      template: item.buttons?.length ? { buttons: item.buttons } : null,
                      quick_replies: [],
                    },
                    buttonsClicked: wasAnswered,
                    ts: i,
                  }
                }
                return {
                  role: 'user',
                  text: item.text,
                  fileAttachment: item.attachments?.[0]
                    ? { name: item.attachments[0].file_name, mimeType: item.attachments[0].mime_type, size: null }
                    : null,
                  ts: i,
                }
              })
            : []

          if (historyMessages.length > 0) {
            setMessages(() => appendBotResponse(historyMessages, data.response))
          } else {
            setMessages(prev => appendBotResponse(prev, data.response))
          }
          setIsBotTyping(false)
          setIsProcessingResume(data.session?.state === 'processing_resume')

          if (data.session?.state === 'pre_screen_evaluating') {
            startEvaluationCountdown()
          }
        }

        if (data.type === 'webchat_error') {
          setIsBotTyping(false)
          setIsProcessingResume(false)
          clearEvaluationCountdown()
        }
      } catch (e) {
        // ignore non-json frames
      }
    }

    ws.onerror = () => setIsBotTyping(false)

    ws.onclose = () => {
      stopHeartbeat()

      if (!shouldReconnectRef.current) return

      setIsBotTyping(false)
      setIsReconnecting(true)

      const attempt = reconnectAttemptsRef.current + 1
      reconnectAttemptsRef.current = attempt
      const delay = Math.min(1000 * 2 ** (attempt - 1), MAX_RECONNECT_DELAY_MS)

      reconnectTimeoutRef.current = setTimeout(() => {
        if (shouldReconnectRef.current) connectWs()
      }, delay)
    }
  }, [jobId, wsUrl, clientId, startEvaluationCountdown, clearEvaluationCountdown, appendBotResponse, startHeartbeat, stopHeartbeat])

  useEffect(() => {
    if (!jobId) return

    shouldReconnectRef.current = true
    reconnectAttemptsRef.current = 0
    connectWs()

    return () => {
      shouldReconnectRef.current = false
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      wsRef.current?.close()
      clearEvaluationCountdown()
      stopHeartbeat()
    }
  }, [jobId, connectWs, clearEvaluationCountdown, stopHeartbeat])

  const sendMessage = useCallback((textOverride, displayTextOverride) => {
    const text = (textOverride !== undefined ? textOverride : inputText).trim()
    const displayText = (
      displayTextOverride !== undefined ? displayTextOverride
        : textOverride !== undefined ? textOverride
          : inputText
    ).trim()
    const hasAttachment = !!pendingAttachment

    if (!text && !hasAttachment) return
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return

    const extId = externalUserId || `webchat-${clientId}`

    const clientMessageId = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `cm-${Date.now()}-${Math.random().toString(16).slice(2)}`

    const payload = {
      action: 'webchat_message',
      client_message_id: clientMessageId,
      external_user_id: extId,
      text: text || 'Uploaded a file',
      metadata: { source: 'apply_page' },
    }

    if (wsSessionToken) payload.ws_session_token = wsSessionToken

    if (pendingAttachment) {
      payload.attachments = [{
        mime_type: pendingAttachment.mimeType,
        content_base64: pendingAttachment.base64,
        file_name: pendingAttachment.name,
        name: pendingAttachment.name,
      }]
    }

    if (refToken) {
      payload.ref_token = refToken
    } else if (jobId) {
      payload.job_id = jobId
      payload.metadata = { ...payload.metadata, job_id: jobId }
    }

    wsRef.current.send(JSON.stringify(payload))

    setMessages(prev => [...prev, {
      role: 'user',
      text: displayText || '',
      fileAttachment: pendingAttachment ? { name: pendingAttachment.name, size: pendingAttachment.size, mimeType: pendingAttachment.mimeType } : null,
      ts: Date.now(),
    }])
    setIsBotTyping(true)
    setInputText('')
    setPendingAttachment(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [inputText, pendingAttachment, externalUserId, clientId, refToken, wsSessionToken, jobId])

  const handleButtonClick = useCallback((msgIdx, btnPayload, label) => {
    setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, buttonsClicked: true } : m))
    sendMessage(btnPayload, label)
  }, [sendMessage])

  const processFile = async (file) => {
    if (!file) return
    const allowedMime = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]
    const isAllowed = allowedMime.includes(file.type) || /\.(pdf|docx)$/i.test(file.name)
    if (!isAllowed) return

    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('read failed'))
        reader.readAsDataURL(file)
      })
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl
      setPendingAttachment({
        name: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
        base64,
      })
    } catch (e) {
      // ignore file read errors
    }
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (file) await processFile(file)
    e.target.value = ''
  }

  const removeAttachment = () => {
    setPendingAttachment(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <div className={`chart-container${IS_IOS ? ' chart-container--ios' : ''}`} ref={containerRef}>
      {/* Left: Chat Area */}
      <div className="chat-area">
        <div className="chat-header">
          <div className="chat-header__brand">
            <div className="brand-icon">
              {/* <LightningIcon /> */}
              <img src='/images/gappeoai-sm-logo.svg' alt='' />
            </div>
            <div className="brand-info">
              <h2 className="brand-name">Gappeo</h2>
              <div className="brand-status">
                <span className={`status-dot${isReconnecting ? ' status-dot--reconnecting' : ''}`} />
                <span className="status-text">{isReconnecting ? 'Reconnecting...' : 'AI Hiring Assistant'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="chat-messages" ref={chatMessagesRef}>
          {messages.map((msg, idx) => {
            if (msg.role === 'bot') {
              const response = msg.response || {}
              const buttons = [
                ...(response?.template?.buttons || []),
                ...(response?.quick_replies || []),
              ]
              return (
                <div key={idx} className="message">
                  <div className="message__avatar">
                    {/* <LightningIcon /> */}
                    <img src='/images/gappeoai-sm-logo.svg' alt='' />
                  </div>
                  <div className="message__bubble">
                    {response?.template?.text?.trim() || response?.text || ''}
                    {buttons.length > 0 && (
                      <div className="message-buttons">
                        {buttons.map((b, bIdx) => {
                          const label = b.title || b.label || b.payload || `Option ${bIdx + 1}`
                          if (b.type === 'web_url' && b.url) {
                            return (
                              <a
                                key={bIdx}
                                className="message-btn"
                                href={b.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  // Facebook/Instagram/Messenger's in-app browser can't
                                  // reliably grant microphone access, and gives the
                                  // candidate no way to fix it if it's blocked -- so
                                  // send this link straight to the device's real
                                  // browser instead of letting it open in-app.
                                  if (!IS_IN_APP_BROWSER) return
                                  e.preventDefault()
                                  openExternally(b.url)
                                }}
                              >
                                {label}
                              </a>
                            )
                          }
                          const btnPayload = b.payload || label
                          return (
                            <button
                              key={bIdx}
                              className="message-btn"
                              disabled={!!msg.buttonsClicked}
                              onClick={() => handleButtonClick(idx, btnPayload, label)}
                            >
                              {label}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )
            }

            return (
              <div key={idx} className="message message--user">
                <div className="message__bubble message__bubble--user">
                  {msg.text || null}
                  {msg.fileAttachment && (
                    <div className="file-attachment">
                      <div className="file-attachment__icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div className="file-attachment__info">
                        <span className="file-attachment__name">{msg.fileAttachment.name}</span>
                        <span className="file-attachment__type">{msg.fileAttachment.mimeType === 'application/pdf' ? 'PDF' : 'DOCX'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {isBotTyping && (
            <div className="message">
              <div className="message__avatar">
                {/* <LightningIcon /> */}
                <img src='/images/gappeoai-sm-logo.svg' alt='' />
              </div>
              <div className="message__bubble message__bubble--typing">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            </div>
          )}
        </div>

        {isProcessingResume && (
          <div className="resume-processing">
            <span className="resume-processing__text">Processing your resume...</span>
            <div className="resume-processing__track">
              <span className="resume-processing__bar" />
            </div>
          </div>
        )}

        {evaluationCountdown !== null && (
          <div className="resume-processing">
            <span className="resume-processing__text">Evaluating your responses, please wait... {evaluationCountdown}s</span>
            <div className="resume-processing__track">
              <span className="resume-processing__bar" />
            </div>
          </div>
        )}

        {pendingAttachment && (
          <div className="pending-attachment">
            <span className="pending-attachment__name">{pendingAttachment.name}</span>
            <button className="pending-attachment__remove" type="button" onClick={removeAttachment}>
              ×
            </button>
          </div>
        )}

        <div className="chat-input-area" ref={chatInputAreaRef}>
          <button
            className="attach-btn"
            type="button"
            // On iOS, tapping this button blurs the focused input first,
            // which kicks off our pin-release/--gp-vh-offset-reset layout
            // changes mid-tap -- the button shifts under the user's finger
            // between touchstart and touchend, and iOS cancels the
            // synthetic click when that happens (the tap silently does
            // nothing; a second tap on the now-settled layout works).
            // Preventing the mousedown's default stops the blur from firing
            // in the first place, so nothing moves and the tap always lands.
            onMouseDown={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachIcon />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="visually-hidden-file-input"
            onChange={handleFileChange}
          />
          <input
            ref={chatInputRef}
            type="text"
            className="chat-input"
            placeholder="Type your answer..."
            value={inputText}
            onChange={e => { setInputText(e.target.value); notePinnedActivity() }}
            onKeyDown={e => { if (e.key === 'Enter') sendMessage() }}
            onFocus={e => startKeyboardPolling(e.target)}
            onBlur={stopKeyboardPolling}
            onClick={e => {
              // If a gesture (not a tap elsewhere) dismissed the keyboard
              // without blurring the input, no 'focus' event fires when the
              // user taps it again to keep typing -- only this click does.
              // Re-arm the same detection cycle as a fresh focus would.
              if (isPinnedAboveKeyboardRef.current) startKeyboardPolling(e.target)
            }}
          />
          <button
            className="send-btn"
            type="button"
            // Same fix as attach-btn above -- keeps the input focused so the
            // tap isn't cancelled by a mid-tap layout shift on iOS.
            onMouseDown={e => e.preventDefault()}
            onClick={() => sendMessage()}
          >
            <SendIcon />
          </button>
        </div>
      </div>

    </div>
  )
}

export default Chat
